import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  MessageCircle, CheckCircle2, XCircle, Loader2, RotateCw,
  AlertTriangle, Clock,
} from "lucide-react";
import { hasPerm, isSuperAdmin } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  enviado: { icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50", label: "Enviado" },
  falhou: { icon: XCircle, cls: "text-rose-700 bg-rose-50", label: "Falhou" },
  pendente: { icon: Clock, cls: "text-amber-700 bg-amber-50", label: "Pendente" },
  nao_configurado: { icon: AlertTriangle, cls: "text-slate-600 bg-slate-100", label: "Não configurado" },
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" });
  } catch { return iso; }
}
function fmtTel(t) {
  const d = String(t || "").replace(/\D/g, "");
  if (d.length === 13 && d.startsWith("55")) return `+55 (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return t;
}

/**
 * WhatsAppLogs — página de auditoria dos envios de WhatsApp automáticos.
 * Filtro por status, botão de reenvio pra mensagens que falharam.
 * fev/2026: Bloco 5 do pedido Z-API.
 */
export default function WhatsAppLogs() {
  const { user } = useAuth();
  const canResend = isSuperAdmin(user) || hasPerm(user, "whatsapp_logs", "a");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [resendingId, setResendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const r = await api.get(`/whatsapp/logs${p}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar log");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // `load` fecha sobre `statusFilter` já nas deps; nada mais dinâmico.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const doResend = async (id) => {
    setResendingId(id);
    try {
      const r = await api.post(`/whatsapp/resend/${id}`);
      if (r.data?.ok) toast.success("Reenviado com sucesso");
      else toast.error(`Falhou: ${r.data?.error || "erro"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao reenviar");
    } finally { setResendingId(null); }
  };

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-3xl font-extrabold flex items-center gap-3">
          <MessageCircle className="w-8 h-8 text-emerald-600" />Log de Mensagens WhatsApp
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Histórico de envios automáticos disparados pela integração Z-API.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-4 flex-wrap">
            <span>Envios recentes</span>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Status:</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-40" data-testid="wa-log-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="enviado">Enviado</SelectItem>
                  <SelectItem value="falhou">Falhou</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="nao_configurado">Não configurado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="wa-log-refresh">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : "↻"} Atualizar
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 && !loading && (
            <div className="text-sm text-muted-foreground py-10 text-center" data-testid="wa-log-empty">
              Nenhum envio registrado ainda.
            </div>
          )}
          {rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tentativas</TableHead>
                  <TableHead>Erro</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pendente;
                  const IconStatus = meta.icon;
                  const canResend = r.status === "falhou" || r.status === "nao_configurado";
                  return (
                    <TableRow key={r.id} data-testid={`wa-log-row-${r.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                      <TableCell className="text-sm">{r.cliente_nome || "—"}</TableCell>
                      <TableCell className="text-sm mono">{fmtTel(r.phone)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${meta.cls}`}>
                          <IconStatus className="w-3 h-3" />{meta.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{r.retry_count || 0}</TableCell>
                      <TableCell className="text-xs text-rose-700 max-w-[200px] truncate" title={r.error}>
                        {r.error || ""}
                      </TableCell>
                      <TableCell className="text-right">
                        {canResend && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => doResend(r.id)}
                            disabled={resendingId === r.id}
                            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`wa-log-resend-${r.id}`}
                          >
                            {resendingId === r.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCw className="w-3 h-3 mr-1" />}
                            Reenviar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
